import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Markdown from 'components/Shared/Markdown';
import FormattingGuideDrawer from 'components/TextProjects/FormattingGuideDrawer/FormattingGuideDrawer';
import styles from './MarkdownField.module.scss';

/**
 * Markdown textarea with Write / Preview tabs and an on-demand formatting
 * guide. Preview uses the same Markdown + embed pipeline as the live page.
 * Used for project descriptions and the portfolio bio.
 */
const MarkdownField = ({
  id,
  label,
  labelClassName,
  value,
  onChange,
  disabled,
  error,
  placeholder,
  rows,
  maxLength,
  previewCaption,
  help,
  guideSubline,
  onGuideOpenChange,
}) => {
  const [mode, setMode] = useState('write');
  const [guideOpen, setGuideOpen] = useState(false);
  const rootRef = useRef(null);
  const textareaRef = useRef(null);
  const guideTriggerRef = useRef(null);
  const selectionRef = useRef({ start: null, end: null, scrollTop: 0 });
  const previousModeRef = useRef(mode);

  const saveTextareaState = useCallback(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    selectionRef.current = {
      start: el.selectionStart,
      end: el.selectionEnd,
      scrollTop: el.scrollTop,
    };
  }, []);

  const setWriteMode = useCallback(() => {
    setMode('write');
  }, []);

  const setPreviewMode = useCallback(() => {
    saveTextareaState();
    setMode('preview');
  }, [saveTextareaState]);

  useEffect(() => {
    const wasPreview = previousModeRef.current === 'preview';
    previousModeRef.current = mode;

    if (mode !== 'write' || !wasPreview) {
      return;
    }

    const el = textareaRef.current;
    if (!el) {
      return;
    }

    const { start, end, scrollTop } = selectionRef.current;
    el.focus();
    el.scrollTop = scrollTop;
    if (start != null && end != null) {
      try {
        el.setSelectionRange(start, end);
      } catch (e) {
        // Ignore if selection restore fails (e.g. value shrunk).
      }
    }
  }, [mode]);

  const setGuide = useCallback(
    (open) => {
      setGuideOpen(open);
      if (onGuideOpenChange) {
        onGuideOpenChange(open);
      }
    },
    [onGuideOpenChange]
  );

  const openGuide = useCallback(() => setGuide(true), [setGuide]);
  const closeGuide = useCallback(() => setGuide(false), [setGuide]);

  const hasContent = Boolean(value && value.trim());

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.header}>
        <label className={labelClassName || styles.label} htmlFor={id}>
          {label}
        </label>
        <div
          className={styles.tabs}
          role="tablist"
          aria-label={`${label} mode`}
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'write'}
            className={mode === 'write' ? styles.tabActive : styles.tab}
            onClick={setWriteMode}
            disabled={disabled}
          >
            Write
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'preview'}
            className={mode === 'preview' ? styles.tabActive : styles.tab}
            onClick={setPreviewMode}
            disabled={disabled}
          >
            Preview
          </button>
        </div>
      </div>

      {mode === 'write' ? (
        <textarea
          ref={textareaRef}
          id={id}
          className={styles.textarea}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onSelect={saveTextareaState}
          onBlur={saveTextareaState}
          onScroll={saveTextareaState}
          disabled={disabled}
          rows={rows}
          maxLength={maxLength}
        />
      ) : (
        <div className={styles.previewWrap}>
          <div className={styles.preview}>
            {hasContent ? (
              <Markdown text={value} />
            ) : (
              <p className={styles.previewEmpty}>Nothing to preview yet.</p>
            )}
          </div>
          {hasContent && previewCaption && (
            <p className={styles.previewCaption}>{previewCaption}</p>
          )}
        </div>
      )}

      <div className={styles.footer}>
        <p className={styles.footerHelp}>{help}</p>
        <button
          ref={guideTriggerRef}
          type="button"
          className={styles.footerLink}
          onClick={openGuide}
          disabled={disabled}
        >
          Formatting guide
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <FormattingGuideDrawer
        open={guideOpen}
        onClose={closeGuide}
        triggerRef={guideTriggerRef}
        retainRef={rootRef}
        subline={guideSubline}
      />
    </div>
  );
};

MarkdownField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  /** Replaces the default label style, e.g. to match a dialog's labels. */
  labelClassName: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  placeholder: PropTypes.string,
  rows: PropTypes.number,
  maxLength: PropTypes.number,
  previewCaption: PropTypes.string,
  help: PropTypes.node,
  guideSubline: PropTypes.string,
  /** Lets a surrounding modal ignore Esc while the guide is open. */
  onGuideOpenChange: PropTypes.func,
};

MarkdownField.defaultProps = {
  labelClassName: null,
  value: '',
  disabled: false,
  error: null,
  placeholder: '',
  rows: 6,
  maxLength: undefined,
  previewCaption: null,
  help: 'Markdown and embeds work here.',
  guideSubline: undefined,
  onGuideOpenChange: null,
};

export default MarkdownField;
